// The three words a viewer picks video quality with.
//
// The player already knows the real HLS rendition ladder — 428x214, 640x320,
// 1920x960 and whatever else the encoder produced — and used to put it on the
// control bar as a chip. That was built for testing playback, not for
// watching: a viewer does not know which rung their connection can hold, and
// naming rungs invites them to pick one that stalls. The BBC offers three
// words instead, so this module maps a ladder onto those three.
//
// It is deliberately pure and player-free: the mapping is the part with rules
// in it, so it is testable without a video element, an HLS stream or a DOM.

import type { QualityLevelInfo } from './surfaceTypes'

export type QualityTier = 'low' | 'medium' | 'highest'

export const QUALITY_TIER_LABELS: Record<QualityTier, string> = {
  low: 'Low',
  medium: 'Medium',
  highest: 'Highest available',
}

// Rungs are ranked by pixel area, with bitrate as the tie-break.
//
// AREA, NOT HEIGHT. A 360 master is 2:1 equirectangular while the flat
// fallback is 16:9, so height alone ranks the two ladders differently; area
// ranks by how much picture the rung actually carries, which is the thing that
// costs bandwidth. A master playlist that declared no resolution leaves area
// at 0, and bitrate then does the ordering on its own.
function rank(level: QualityLevelInfo): [number, number] {
  const area = (level.width || 0) * (level.height || 0)
  return [area, level.bitrate || 0]
}

/**
 * Maps a rendition ladder onto the three tiers.
 *
 * `highest` is always `'auto'`: the point of the top tier is that ABR picks,
 * so pinning it to the top rung would be a worse offer than the default —
 * it would stall rather than step down when the connection dips.
 *
 * `low` is the smallest rung. `medium` is the median rung by pixel area,
 * taking the LOWER middle when the ladder has an even number of rungs: a tier
 * named Medium should never be one rung short of the top, and a viewer who
 * reaches for it is asking to spend less, not more. With two rungs that
 * collapses Medium onto Low, which is honest — a two-rung ladder has no
 * middle, and the alternative is a Medium that is really the maximum.
 *
 * An empty ladder means native HLS (iOS), where no rungs are exposed at all
 * and the browser is choosing quality regardless. Every tier resolves to
 * `'auto'` there, so the menu still renders and every choice tells the truth.
 */
export function tierTargets(
  levels: QualityLevelInfo[],
): Record<QualityTier, 'auto' | number> {
  if (levels.length === 0) {
    return { low: 'auto', medium: 'auto', highest: 'auto' }
  }

  const sorted = [...levels].sort((a, b) => {
    const [aArea, aBitrate] = rank(a)
    const [bArea, bBitrate] = rank(b)
    return aArea - bArea || aBitrate - bBitrate
  })

  const median = sorted[Math.floor((sorted.length - 1) / 2)]

  return {
    low: sorted[0].index,
    medium: median.index,
    highest: 'auto',
  }
}
